import type {
  ChangeEventHandler,
  HTMLProps,
} from "react"
import {
  useMemo,
  useCallback,
  useEffect,
} from "react"
import { useFormContext } from "./FormContext"

export type FormInput =
  HTMLInputElement |
  HTMLTextAreaElement |
  HTMLSelectElement

export function useFormInputProps<
  T extends FormInput,
  Props extends HTMLProps<T>,
>(props: Props): Props {
  const formContext = useFormContext()

  // recheck validity when:
  // - input mounts
  // - input is disabled or enabled
  // - input unmounts
  useEffect(
    () => {
      formContext?.recheckValidity()

      return () => {
        formContext?.recheckValidity()
      }
    },
    [
      props.disabled,
      formContext?.recheckValidity,
    ],
  )

  // recheck validity when control value changes
  useEffect(
    () => {
      if (props.value !== undefined) {
        formContext?.recheckValidity()
      }
    },
    [
      props.value,
      formContext?.recheckValidity,
    ],
  )

  // recheck validity when control checked changes
  useEffect(
    () => {
      if (props.checked !== undefined) {
        formContext?.recheckValidity()
      }
    },
    [
      props.checked,
      formContext?.recheckValidity,
    ],
  )

  // recheck validity onChange if using uncontrolled component
  const onChange: ChangeEventHandler<T> = useCallback(
    (e) => {
      props.onChange?.(e)

      // TODO do we need to check props.checked ?
      if (props.value === undefined) {
        formContext?.recheckValidity()
      }
    },
    [
      props.value,
      props.onChange,
      formContext?.recheckValidity,
    ],
  )

  return useMemo(
    () => ({
      ...props,
      onChange,
    }),
    [
      props,
      onChange,
    ],
  )
}
